/**
 * Hold a value still until it stops changing.
 *
 * SHARED hook (`src/hooks/`), not a feature-local one. It lived in
 * `features/amazonAds/hooks/` and picked up a cross-feature deep import from
 * `categoryCollection`, while `pages/Orders.tsx` and
 * `features/salesKpi/hooks/useSalesKpiQueries.ts` each carried their own
 * byte-equivalent private copy — Orders' even said "promote when a second caller
 * needs it". Every consumer now imports this one: all six search boxes on the
 * Amazon Ads screen, Orders, Sales KPI, and Burdened Cost's SKU/title search.
 *
 * ## The server-side boxes
 *
 * A search box wired straight into a fetch dependency list makes every keystroke
 * a request. Typing "cutlery" fired seven queries at a table running to millions
 * of rows, and because those filters are PREFIX or CONTAINS matches the
 * intermediate ones ("c", "cu", "cut"…) match progressively enormous row counts —
 * the short ones are the most expensive, and every one is thrown away.
 *
 * Correctness was never the issue: react-query and `useAsyncData` both discard a
 * response whose request has been superseded, so a grid could not show the wrong
 * rows. This is purely about not asking the database seven questions to get one
 * answer.
 *
 * ## Amazon Ads' two in-browser boxes
 *
 * The campaign search and the period pickers' filter box issue no request at
 * all, and they are debounced for two different reasons:
 *
 *  - **Campaign search.** Its value sits in a `useMemo` dependency list
 *    ALONGSIDE the payload, so each character re-ran the whole `campaignRows`
 *    aggregation over the ad-fact rows (~100k for a default window), then
 *    re-filtered, then re-sorted. That is real work per keystroke.
 *  - **`BrandSelect`'s filter box.** A few hundred labels already in memory, so
 *    there is nothing to save — it is debounced because the same 400ms applies
 *    to every search box on the screen and a box that behaved differently from
 *    its neighbours would be the surprise. It clears INSTANTLY (see the note in
 *    `BrandSelect`), which is what keeps a reopened menu from showing the last
 *    filter.
 *
 * In both cases the INPUT stays controlled by the raw value, so the characters
 * appear as they are typed; only the work done with them waits.
 */
import { useEffect, useState } from 'react';

/**
 * Long enough to swallow a typing burst, short enough not to feel laggy.
 *
 * 400ms, chosen so an average typist's whole word lands as one search. It was
 * 350ms when only the server-side boxes used this.
 */
export const FILTER_DEBOUNCE_MS = 400;

export function useDebouncedValue<T>(value: T, delayMs: number = FILTER_DEBOUNCE_MS): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delayMs);
    // Clearing on every change is what makes this a debounce rather than a
    // throttle: the timer only ever fires once the value has stopped moving.
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
